/**
 * Contrato do código de produto, compartilhado por três consumidores que
 * precisam concordar exatamente sobre o formato: a etiqueta impressa (gera o
 * QR), o leitor do PDV (lê o QR) e o servidor (resolve o código para produto).
 *
 * Se qualquer um deles divergir, nenhuma etiqueta já impressa é lida — e a
 * falha só aparece depois da impressão. Por isso o formato mora aqui, e não
 * duplicado em cada app.
 */

/** Prefixo do payload do QR. Evita confundir QR alheio com código de produto. */
export const PREFIXO_CODIGO_PRODUTO = 'LEO:';

/** Largura do código gerado automaticamente pela sequence do banco. */
export const TAMANHO_CODIGO_SEQUENCIAL = 5;

/**
 * Um código aceitável: começa por letra ou dígito, admite ponto, hífen e
 * underscore, no máximo 16 caracteres. Barra, dois-pontos e espaço ficam de
 * fora justamente para descartar URLs e texto solto lidos por engano.
 */
const CODIGO_VALIDO = /^[A-Z0-9][A-Z0-9._-]{0,23}$/;

/** `00010-3`: código do produto, hífen, número da peça. */
const CODIGO_UNIDADE = /^(.+)-([1-9]\d*)$/;

const SOMENTE_DIGITOS = /^\d+$/;

/** Formata o valor vindo da sequence do Postgres como `00001`. */
export function formatarCodigoSequencial(numero: number): string {
  if (!Number.isInteger(numero) || numero < 1) {
    throw new Error(`Número de sequência inválido para código de produto: ${numero}`);
  }
  return String(numero).padStart(TAMANHO_CODIGO_SEQUENCIAL, '0');
}

/** Apara, remove o prefixo se houver e normaliza a caixa. Não valida. */
function limpar(valor: string): string {
  const aparado = valor.trim().toUpperCase();
  return aparado.startsWith(PREFIXO_CODIGO_PRODUTO)
    ? aparado.slice(PREFIXO_CODIGO_PRODUTO.length).trim()
    : aparado;
}

/** Monta o conteúdo do QR impresso na etiqueta. */
export function montarPayloadQr(codigo: string): string {
  const normalizado = extrairCodigo(codigo);
  if (!normalizado) {
    throw new Error(`Código de produto inválido para etiqueta: "${codigo}"`);
  }
  return `${PREFIXO_CODIGO_PRODUTO}${normalizado}`;
}

/**
 * Converte o que a câmera leu (ou o caixa digitou) em código de produto.
 * Devolve `null` quando o valor não tem cara de código — um QR de embalagem,
 * um cartaz ou um crachá não podem virar uma venda.
 */
export function extrairCodigo(valorLido: string): string | null {
  const candidato = limpar(valorLido);
  return CODIGO_VALIDO.test(candidato) ? candidato : null;
}

/**
 * Formas sob as quais o código pode estar gravado no banco.
 *
 * Digitar `42` no PDV precisa achar o produto `00042`, mas um administrador
 * pode ter cadastrado o código `42` literalmente (R3). Em vez de escolher uma
 * das duas interpretações aqui, devolvemos as duas e deixamos a consulta
 * decidir pelo que existe.
 */
export function variantesCodigo(valorLido: string): string[] {
  const codigo = extrairCodigo(valorLido);
  if (!codigo) return [];

  if (SOMENTE_DIGITOS.test(codigo) && codigo.length < TAMANHO_CODIGO_SEQUENCIAL) {
    return [codigo, codigo.padStart(TAMANHO_CODIGO_SEQUENCIAL, '0')];
  }

  return [codigo];
}

/**
 * Código de uma peça individual, derivado do código do produto.
 *
 * Derivar em vez de usar uma sequência própria é o que faz a etiqueta dizer a
 * que produto pertence só de olhar — conferir uma pilha de camisetas M na hora
 * de colar fica trivial.
 */
export function montarCodigoUnidade(codigoProduto: string, sequencia: number): string {
  if (!Number.isInteger(sequencia) || sequencia < 1) {
    throw new Error(`Sequência de peça inválida: ${sequencia}`);
  }
  return `${codigoProduto}-${sequencia}`;
}

/**
 * Quebra o código da peça de volta em produto e sequência.
 *
 * Devolve `null` para um código sem sufixo — esse é código de produto, não de
 * peça, e quem chama decide o que fazer com ele (no PDV, pegar a próxima peça
 * disponível).
 */
export function separarCodigoUnidade(
  codigo: string
): { codigoProduto: string; sequencia: number } | null {
  const normalizado = extrairCodigo(codigo);
  if (!normalizado) return null;

  const partes = CODIGO_UNIDADE.exec(normalizado);
  if (!partes) return null;

  return { codigoProduto: partes[1]!, sequencia: Number(partes[2]) };
}
